/**
 * This file contains the colors as described in our System Design
 * @link https://www.figma.com/design/anJykRaoHi8nXjqpqlynmd/Product-Design-System?node-id=165-46&p=f&t=YrmLZBVlOesudg5a-0
 */

export const black = '#000000';

export const white = '#FFFFFF';

export const purple = {
    50: '#F8F6F9',
    100: '#E7DCE7',
    200: '#C2A7C3',
    300: '#9D74A0',
    400: '#7E4781',
    500: '#5C1761',
    600: '#4E1453',
    700: '#401044',
    800: '#370E3B',
    900: '#2D0B30',
};
/**
 * Purple at reduced opacity, for a mark laid over content that has to stay readable through it.
 *
 * `purple[500]` carrying an alpha byte, so the colour each one is made of is legible in the value
 * itself rather than restated as channels that could drift from the ramp above.
 *
 * Keyed by the opacity it is meant to read as. The byte is that opacity in 255ths, which is why
 * 10% is `1A` rather than a round number -- the rendered alpha lands within a fifth of a percent
 * of the key, which no eye resolves.
 *
 * A solid tint is not an alternative here: these cover the very thing they are drawn to point at,
 * so whatever is underneath has to show through.
 */
export const purpleWash = {
    10: '#5C17611A',
    12: '#5C17611F',
    25: '#5C176140',
};

export const red = {
    50: '#FFF8F9',
    100: '#FEE6E7',
    200: '#FDB7BA',
    300: '#FB8B90',
    400: '#FB7479',
    500: '#FA5A60',
    600: '#ED4F52',
    700: '#DF4343',
    800: '#AD383A',
    900: '#7B2C2F',
};
export const orange = {
    50: '#FFFAF7',
    100: '#FFF0E5',
    200: '#FFD4B7',
    300: '#FFB888',
    400: '#FFA972',
    500: '#FF995B',
    600: '#F68C56',
    700: '#EC7E50',
    800: '#B76640',
    900: '#7D4B2D',
};

/**
 * 1-800Accountant brand orange — used only on partner co-branded surfaces (the partner
 * scheduler CTAs). Intentionally separate from the Collective `orange` scale above.
 */
export const partnerOrange = {
    main: '#E9671E',
    dark: '#C9560F',
    light: '#F2946A',
};
/**
 * The lighter brand purple design is moving primary CTAs to. Scoped to the Accounting Setup V1 CTAs
 * for now; `primary` stays purple[500] until the rest of the product moves with it.
 * @deprecated Use the Croissant brand tokens (`croissantColors.background.brand.*`,
 * `croissantColors.text.brand.*`) for new UI.
 */
export const brandPurple = {
    main: '#87369B',
    hover: '#A146B8',
    dark: purple[500],
    // Figma's #F0E2F3 at 50%, flattened onto white so its contrast doesn't depend on the surface beneath.
    tint: '#F7F0F9',
};
export const green = {
    50: '#F2F9F7',
    100: '#D9EDE8',
    200: '#92CCBD',
    300: '#4DAC94',
    400: '#289B7E',
    500: '#008866',
    600: '#017B5C',
    700: '#006D52',
    800: '#015943',
    900: '#004332',
};
export const grey = {
    50: '#F3F3F3',
    100: '#E2E2E2',
    200: '#CCCCCC',
    300: '#AFAFAF',
    400: '#969696',
    500: '#787878',
    600: '#606060',
    700: '#474747',
    800: '#303030',
    900: '#1B1B1B',
};
export const cement = {
    50: '#FAF9F6',
    100: '#F6F3EB',
    200: '#EFECE4',
    300: '#E5E0D2',
    400: '#CECABD',
    500: '#A3998F',
    600: '#857D75',
    700: '#625C57',
    800: '#383531',
    900: '#212121',
};

export const outlineBlue = '#5E9ED6';

/**
 * Stops for the QTE dashboard hero's "estimate due" card gradient (Quarterly Payments redesign).
 * Deliberately not a palette scale: these two blues are pending design-system sign-off, and the
 * gradient in `mixins.ts` is their only consumer. Promote them to `palette.ts` if design adopts a
 * blue scale.
 */
export const qteHeroGradientBlue = {
    from: '#A0DDEF',
    to: '#E9F2F5',
};

/**
 * Pale blue and violet card fills from the Accounting Setup V1 designs. Not palette scales,
 * matching `qteHeroGradientBlue` above: promote them if the design system adopts them.
 */
export const pendingTints = {
    blue: 'rgba(219, 239, 250, 0.5)',
    violet: '#F9F6FE',
};

/**
 * Per-jurisdiction fills for the QTE estimate cards' progress bar (federal blue,
 * state violet), so the two cards read as distinct at a glance.
 *
 * `covered` is the light leading segment — withholding and payments already made
 * — and `dueNow` the darker segment for what is still left to pay, butting up
 * against it. Flat tones rather than gradients: the two segments must read as a
 * boundary at their join, which a gradient running under both would blur.
 *
 * Deliberately not a palette scale, matching `qteHeroGradientBlue` above: these
 * are pending design-system sign-off and the estimate card is their only
 * consumer. Values sampled from Figma node 9554-46090, which restyled the bar
 * from the original 9827-60677.
 */
export const qteEstimateFills = {
    federal: { covered: '#8cd0f8', dueNow: '#0882c9' },
    state: { covered: '#d2a6dd', dueNow: '#8b5399' },
};

/**
 * Behind both segments. The pair normally covers it completely — they are two
 * shares of one total — so it shows only when there is nothing to divide: with
 * nothing covered and nothing left to pay the bar would otherwise vanish, and
 * the card's layout would jump.
 */
export const qteEstimateTrack = '#fbfbfb';
